import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useQueryClient } from '@tanstack/react-query';

import {
  FeedGalleryPayload, GalleryImage, getGalleryGroup,
} from '../../services/gallery_api';
import GalleryLightbox from './GalleryLightbox';

interface Props {
  gallery: FeedGalleryPayload;
  isPublic?: boolean;
}

/**
 * アクティビティフィードのギャラリー投稿サムネ帯（最大4枚=2×2＋「+N」）。
 * サムネ/+N クリックで共通ライトボックスをグループ全画像で開く（リアクション込み）。
 * ギャラリータブへの導線は親ヘッダー側（「N枚アップロード」テキスト横）に置く。
 */
const GalleryActivityCard: React.FC<Props> = ({ gallery, isPublic }) => {
  const queryClient = useQueryClient();
  const [groupImages, setGroupImages] = useState<GalleryImage[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // オーナー（自分のギャラリー閲覧）以外はネタバレ画像をぼかす
  const isOwner = !isPublic;
  const thumbs = gallery.images.slice(0, 4);
  const extra = gallery.count - thumbs.length; // 「+N」表記に使う超過枚数（5枚以上のとき>0）

  const openAt = async (imageId: number) => {
    if (loading) return;
    setLoading(true);
    try {
      const imgs = await queryClient.fetchQuery({
        queryKey: ['gallery-group', gallery.group_key],
        queryFn: () => getGalleryGroup(gallery.group_key),
        staleTime: 30000,
      });
      setGroupImages(imgs);
      const idx = imgs.findIndex((i) => i.id === imageId);
      setLightboxIndex(idx >= 0 ? idx : 0);
    } catch (e) {
      console.error('ギャラリーグループの取得に失敗しました', e);
    } finally {
      setLoading(false);
    }
  };

  // リアクション更新をライトボックス表示中の画像へ反映
  const handleReaction = (
    imageId: number,
    reactions: { [key: number]: number },
    userReaction: number | null,
  ) => {
    setGroupImages((prev) =>
      prev.map((im) =>
        im.id === imageId ? { ...im, reaction_counts: reactions, user_reaction: userReaction } : im
      )
    );
  };

  // 1枚時のアスペクト比は画像本来の比率を 4:5〜16:9 にクランプ（細長い潰れを防止）
  const singleAspect = (() => {
    const f = thumbs[0];
    const ar = f && f.width && f.height ? f.width / f.height : 16 / 9;
    return Math.max(0.8, Math.min(16 / 9, ar));
  })();

  const tile = (img: typeof thumbs[number], i: number) => {
    const showOverlay = extra > 0 && i === thumbs.length - 1;
    const blurred = img.is_spoiler && !isOwner;
    return (
      <Box
        onClick={() => openAt(img.id)}
        sx={{
          position: 'relative', width: '100%', height: '100%', overflow: 'hidden',
          cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.04)',
          '&:hover img': { transform: blurred ? undefined : 'scale(1.04)' },
        }}
      >
        {img.thumb_url && (
          <Box
            component="img"
            src={img.thumb_url}
            alt={blurred ? '' : (img.caption || '')}
            loading="lazy"
            sx={{
              width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
              filter: blurred ? 'blur(24px)' : 'none',
            }}
          />
        )}
        {/* ネタバレ: 暗幕＋アイコン＋小さいラベル（タップでライトボックス表示） */}
        {blurred && (
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.4,
            color: 'rgba(255,255,255,0.9)',
          }}>
            <VisibilityOffIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.6rem', lineHeight: 1, letterSpacing: '0.05em' }}>ネタバレ</Typography>
          </Box>
        )}
        {showOverlay && (
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 600, fontSize: '1.25rem',
          }}>
            +{extra}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ position: 'relative', maxWidth: 500, borderRadius: '10px', overflow: 'hidden' }}>
        {thumbs.length === 1 && (
          <Box sx={{ width: '100%', aspectRatio: `${singleAspect}`, maxHeight: 420 }}>
            {tile(thumbs[0], 0)}
          </Box>
        )}

        {thumbs.length === 2 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px', aspectRatio: '16 / 9' }}>
            {thumbs.map((im, i) => (
              <Box key={im.id} sx={{ position: 'relative', minWidth: 0 }}>{tile(im, i)}</Box>
            ))}
          </Box>
        )}

        {thumbs.length === 3 && (
          <Box sx={{
            display: 'grid', gridTemplateColumns: '2fr 1fr', gridTemplateRows: '1fr 1fr',
            gap: '3px', aspectRatio: '16 / 9',
          }}>
            <Box sx={{ gridRow: '1 / 3', position: 'relative', minWidth: 0 }}>{tile(thumbs[0], 0)}</Box>
            <Box sx={{ position: 'relative', minWidth: 0 }}>{tile(thumbs[1], 1)}</Box>
            <Box sx={{ position: 'relative', minWidth: 0 }}>{tile(thumbs[2], 2)}</Box>
          </Box>
        )}

        {thumbs.length >= 4 && (
          <Box sx={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr',
            gap: '3px', aspectRatio: '16 / 9',
          }}>
            {thumbs.slice(0, 4).map((im, i) => (
              <Box key={im.id} sx={{ position: 'relative', minWidth: 0 }}>{tile(im, i)}</Box>
            ))}
          </Box>
        )}

        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.25)',
          }}>
            <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.8)' }} />
          </Box>
        )}
      </Box>

      <GalleryLightbox
        images={groupImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
        isPublic={isPublic}
        onReactionUpdate={handleReaction}
      />
    </>
  );
};

export default GalleryActivityCard;
